/* eslint-disable no-console */
import PipelineCardAPI from '../../api/pipelineCards';
import { handlePipelineError } from '../../routes/dashboard/pipeline/helpers/pipelineErrorHandler';
import LeadScoresAPI from '../../api/leadScores';
import DealStatusAPI from '../../api/pipeline/dealStatus';
import {
  PIPELINE_PAGINATION_ERROR_CODE,
  requireNextPipelineCursor,
} from '../../routes/dashboard/pipeline/helpers/pipelinePagination';

// Cache duration for pipeline cards. Um TTL curtíssimo faz o kanban refazer o
// fetch (limit:N por stage) a cada ida e volta de rota, o que pode perder itens
// das páginas seguintes; um TTL longo deixa o board stale.
//
// Eram 5 minutos, justificados por "o realtime atualiza na hora de qualquer
// jeito". Essa premissa não se sustentava: a supressão de eco por usuário
// (RT-P0-1) descartava o evento em todas as abas do autor, e caminhos como o
// menu de contexto da conversa mutavam o card sem invalidar o cache (RT-P0-2).
// Com os dois corrigidos o realtime cobre a janela; 60 s é a rede de segurança
// para o que ele não cobre (queda de WS, evento perdido) sem voltar a refazer
// fetch a cada navegação. (fase-57 RT-P0-3)
const CACHE_DURATION_MS = 60 * 1000; // 1 minute

// Audit 2026-04-25 P0.7 — Map auxiliar para lookup O(1) por id.
// Substitui findIndex linear (custava O(n) com 2000+ items).
// Map global compartilhado entre mutations — não vai pro state reativo
// (Vue 3 não observa Maps profundamente; serve só pra acelerar lookups).
const itemsById = new Map();

const rebuildIndex = items => {
  itemsById.clear();
  items.forEach((item, idx) => itemsById.set(item.id, idx));
};

// Lápides de exclusão (ids removidos nesta "época" de board).
//
// MERGE_PIPELINE_CARD_ITEMS anexa todo id ausente do índice. Como REMOVE tira o
// id do índice, um GET disparado ANTES do DELETE e resolvido DEPOIS re-anexava o
// card excluído — na etapa original, como se nada tivesse acontecido. O guard de
// staleness de fetchStageCards não cobre esse caso: ele compara geração de board,
// usuário e filtros, e excluir um card não muda nenhum dos três.
//
// Relato de cliente 2026-08-11: "excluir pelo board não funciona; só funciona
// abrindo a conversa". O painel da conversa mantém a lista local e não roda os
// GETs paginados por coluna, então só o board ressuscitava o card. O gatilho
// prático é o "carregar mais" de uma coluna com a resposta em voo.
//
// Época: SET_PIPELINE_CARD_ITEMS é um snapshot completo e autoritativo do
// servidor (que já filtra descartados via default_scope), logo abre época nova e
// limpa as lápides. Só MERGE as consulta — é o único caminho que anexa item
// avulso a um board já montado.
const removedItemIds = new Set();

// setItemAt — cria um novo array com o item na posição idx substituído.
// Retorna o novo array; o chamador deve atribuir a _state.items.
//
// ANTES usava splice() in-place. O problema: watchers que observam a
// *referência* do array (ex: `() => props.pipelineCards` no PipelineTab)
// não disparam quando splice muta o array sem trocar a referência. Isso
// causava o bug "mudanças no Pipeline não aparecem em tempo real" —
// UPDATE_ITEM_STAGE, REPLACE_CARD, UPDATE_ITEM_DETAILS silenciosamente
// atualizavam o store mas o kanban não re-renderizava.
// Retornar um novo array garante que a referência muda e o watcher dispara.
const setItemAt = (items, idx, newItem) =>
  items.map((item, i) => (i === idx ? Object.freeze(newItem) : item));

// Audit 2026-05-07 — accountId vem da URL (rota), nunca de um getter que
// possa apontar pra valor stale durante transição de account.
const accountIdFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const segments = window.location.pathname.split('/');
  const idx = segments.indexOf('accounts');
  if (idx === -1) return null;
  const parsed = Number(segments[idx + 1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Ciclo 3b — chave do load de board em andamento (account|pipeline|filtros).
// Module-level (não reativo): serve só para dedupe/supersede de concorrência.
let inFlightBoardKey = null;

// Geração do board. Declarada junto das demais gerações porque
// `invalidatePipelineCardRequests` (logo abaixo) incrementa as três; deixá-la
// mais adiante no módulo criava uma TDZ silenciosa nesse caminho.
// (fetchBoardByStages que passa dos guards de cache/dedupe) incrementa; cada
// fetchStageCards captura a geração no disparo e descarta a resposta se outra
// geração assumiu no meio do voo. Determinístico: NÃO depende do getter
// `pipelines/getSelectedPipeline` na hora do resolve — durante remount/
// transição de rota o getter passa por janela null/stale e descartava
// respostas VÁLIDAS do pipeline correto (colunas presas em "Carregando").
let boardGeneration = 0;

// P1 — requests globais (list/agenda) usam uma chave própria. O `loading`
// também é compartilhado pelo board, portanto não pode servir como dedupe entre
// modos: abrir a lista durante um load do kanban precisa iniciar sua página
// global, não reutilizar silenciosamente os 50 cards por stage.
let inFlightGlobalRequest = null;
let globalGeneration = 0;

// Uma única orquestração consome todas as páginas globais para cada combinação
// account/pipeline/filtros. Dispatches duplicados do bootstrap e do watcher
// aguardam a mesma Promise em vez de encerrar com uma lista parcial.
let inFlightFullLoadKey = null;
let inFlightFullLoadPromise = null;
let fullLoadGeneration = 0;

// Excluir um card invalida as respostas GLOBAIS (lista/agenda) em voo.
//
// A lápide protege o board, que anexa card a card via MERGE. A lista não: ela
// substitui tudo com `SET_PIPELINE_CARD_ITEMS`, que por contrato é um snapshot
// autoritativo e ZERA as lápides. Uma resposta global disparada antes do DELETE
// e resolvida depois passava pelo guard de identidade — ele compara filtro,
// funil, conta, usuário e geração, e excluir não muda nenhum deles — e devolvia
// o card excluído para a tela (achado do cross-review do Codex).
//
// Bumpar só a geração GLOBAL, e não a do board: o board já é resolvido com
// precisão pela lápide, que descarta apenas o card apagado. Invalidá-lo aqui
// jogaria fora a página inteira de um "carregar mais" em voo, e o usuário
// perderia o clique.
const invalidateGlobalListRequests = () => {
  globalGeneration += 1;
  fullLoadGeneration += 1;
  inFlightGlobalRequest = null;
  inFlightFullLoadKey = null;
  inFlightFullLoadPromise = null;
};

const invalidatePipelineCardRequests = () => {
  boardGeneration += 1;
  globalGeneration += 1;
  fullLoadGeneration += 1;
  inFlightBoardKey = null;
  inFlightGlobalRequest = null;
  inFlightFullLoadKey = null;
  inFlightFullLoadPromise = null;
};

const resetIfUserChanged = (commit, localState, currentUserId) => {
  if (
    localState.lastFetchedUserId === null ||
    localState.lastFetchedUserId === undefined ||
    String(localState.lastFetchedUserId) === String(currentUserId)
  ) {
    return false;
  }

  invalidatePipelineCardRequests();
  commit('RESET_FOR_USER_SWITCH');
  return true;
};

const fullLoadSupersededError = () => {
  const error = new Error('Pipeline full load superseded');
  error.code = 'PIPELINE_FULL_LOAD_SUPERSEDED';
  return error;
};

const globalRequestSupersededError = () => {
  const error = new Error('Pipeline global request superseded');
  error.code = 'PIPELINE_GLOBAL_REQUEST_SUPERSEDED';
  return error;
};

const finiteNumberOr = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// A resposta global vem paginada (`{ data, meta }`) ou como array puro,
// dependendo do endpoint. Qualquer outra forma é tratada como vazia.
const extractGlobalItems = responseData => {
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const normalizeGlobalMeta = (rawMeta, itemCount) => {
  const meta =
    rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)
      ? rawMeta
      : {};
  const stageCounts =
    meta.stage_counts &&
    typeof meta.stage_counts === 'object' &&
    !Array.isArray(meta.stage_counts)
      ? meta.stage_counts
      : {};

  return {
    ...meta,
    total: finiteNumberOr(meta.total, itemCount),
    limit: finiteNumberOr(meta.limit, 500),
    offset: finiteNumberOr(meta.offset, 0),
    has_more: Boolean(meta.has_more),
    stage_counts: stageCounts,
    next_cursor: meta.next_cursor || null,
  };
};

// Ciclo 3b — stages visíveis do board: oculta won/lost quando o pipeline
// desabilita as etapas de ganho/perdido (mesma regra do updateColumns no
// PipelineTab). Colunas ocultas não precisam de fetch.
const visibleStageIds = pipeline => {
  const stages = pipeline?.stages || {};
  const showWonLost = pipeline?.settings?.enable_won_lost_stages !== false;
  return Object.keys(stages).filter(id => {
    const stage = stages[id] || {};
    return showWonLost || (!stage.is_won_stage && !stage.is_lost_stage);
  });
};

// Ciclo 8 — ajuste OTIMISTA do badge de contagem da coluna. Um drag entre
// colunas (ou um move remoto) muda o card de stage no flat, mas
// meta.stage_counts (fonte do badge via backendStageCount em
// PipelineColumn.vue) só era atualizado no próximo fetch — o badge ficava
// defasado (QA ciclo 3b, cenário 6). Decrementa a origem / incrementa o
// destino aqui; o stage_counts do SERVIDOR continua autoritativo e reconcilia
// no próximo fetch (toda resposta de fetchStageCards re-emite o mapa inteiro).
// Filtros server-side ativos? stage_counts é filter-aware no servidor; um
// ajuste local cego inflaria/deflaria o badge com um card que pode nem casar
// o filtro (achado Codex ciclo 8) — nesse caso o ajuste otimista é pulado e
// o badge reconcilia no próximo fetch (comportamento pré-ciclo-8).
const hasActiveServerFilters = filters => {
  if (!filters) return false;
  return Object.values(filters).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') {
      return hasActiveServerFilters(value);
    }
    return (
      value !== null && value !== undefined && value !== '' && value !== false
    );
  });
};

const adjustStageCounts = (_state, fromStage, toStage) => {
  if (fromStage === toStage) return;
  if (hasActiveServerFilters(_state.activeFilters)) return;
  const counts = _state.meta?.stage_counts;
  // Sem baseline do servidor (mapa vazio) não há o que ajustar — o badge já
  // cai no fallback local (filteredItems.length na coluna).
  if (!counts || Object.keys(counts).length === 0) return;
  const next = { ...counts };
  if (fromStage && next[fromStage] !== undefined) {
    next[fromStage] = Math.max(0, next[fromStage] - 1);
  }
  if (toStage) {
    // O group(:pipeline_stage).count do backend omite stages zerados, então o
    // destino pode não ter chave — criar com 1 mantém o badge correto.
    next[toStage] = (next[toStage] || 0) + 1;
  }
  // Troca a referência do meta — contrato de reatividade dos computed que
  // observam store.state.pipelineCards.meta (ver setItemAt).
  _state.meta = { ..._state.meta, stage_counts: next };
};

// `selectedItem` é uma CÓPIA do card (lida por usePipelineActions e pelo
// PipelineDashboard de components-next). As mutations de realtime só mexiam em
// `items`, então o card selecionado ficava congelado enquanto o board atrás
// atualizava — quem estava com ele aberto via um estado que já não existia.
// (fase-57 RT-P1-7)
const syncSelectedItem = (_state, cardId, patch) => {
  const selected = _state.selectedItem;
  if (!selected || String(selected.id) !== String(cardId)) return;

  _state.selectedItem = { ...selected, ...patch };
};

const clearSelectedItemIfRemoved = (_state, cardId) => {
  const selected = _state.selectedItem;
  if (!selected || String(selected.id) !== String(cardId)) return;

  _state.selectedItem = null;
};

const state = {
  items: [],
  meta: {
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false,
    stage_counts: {},
    next_cursor: null,
  },
  // Ciclo 3b — metadado de paginação POR STAGE (cursor keyset).
  // Estrutura: { [stageId]: { cursor, hasMore, loading } }.
  // Os ITEMS vivem só no array flat `items` (fonte de verdade única —
  // drag-and-drop, realtime e bulk operam nele); fetchStageCards alimenta o
  // flat via merge com dedupe e guarda aqui apenas o estado do cursor.
  cardsByStage: {},
  selectedItem: null,
  loading: false,
  // Audit 2026-04-25 — flag separada para "carregar mais" (scroll-driven append).
  // O `loading` aciona o skeleton em PipelineTab; usar ele para appends destrói
  // o DOM da kanban e perde a posição de scroll do usuário.
  appending: false,
  error: null,
  lastUpdated: null,
  lastFetchedUserId: null, // Track user to invalidate cache on user switch
  lastFetchedPipelineId: null, // Track pipeline to invalidate cache on pipeline switch
  // Audit 2026-05-07 — invalida cache em troca de account (Vai Pri / ClickHype
  // bug: cards de account 3 vazavam para sessão da account 72 durante
  // transição de rota, gerando rajadas de 404 protetivos no console).
  lastFetchedAccountId: null,
  // NOOVI: filtros do board aplicados server-side (formato aninhado do
  // PipelineFilter.vue). Mudar os filtros invalida o cache via assinatura.
  activeFilters: {},
  lastFetchedFiltersSignature: null,
  // Ciclo 3b — modo do último load: 'board' (50/stage, parcial por design) ou
  // 'global' (lista completa). Um carimbo de board NUNCA pode satisfazer o
  // cache do fetch global (list/agenda renderizariam dados incompletos em
  // silêncio) e vice-versa (board sem cardsByStage perderia o load-more).
  lastFetchedMode: null,
};

const getters = {
  // P0.2 — sem spread (era [..._state.items]). Evita criar array novo de
  // 2000+ elementos a cada acesso. Componentes que precisam de cópia local
  // fazem spread por conta própria.
  getPipelineCardItems: _state => _state.items,
  getMeta: _state => _state.meta,
  // Ciclo 3b — metadado de paginação por stage: { [stageId]: { cursor,
  // hasMore, loading } }. Os items vivem em getPipelineCardItems (flat).
  getCardsByStage: _state => _state.cardsByStage,
  getSelectedItem: _state => _state.selectedItem,
  isLoading: _state => _state.loading,
  getError: _state => _state.error,
  getActiveFilters: _state => _state.activeFilters,
};

// Envia apenas o que o chamador realmente mandou. Duas armadilhas de uma vez:
// `??`/`||` colapsariam "enviado como null" (limpar de verdade) com "omitido",
// e completar as chaves ausentes a partir do cache reenviaria valores velhos —
// desfazendo, numa edição concorrente, o que outra pessoa acabou de salvar.
const pickSupplied = (source, keys) =>
  keys.reduce((payload, key) => {
    if (Object.hasOwn(source ?? {}, key)) payload[key] = source[key];
    return payload;
  }, {});

const actions = {
  setSelectedItem({ commit }, item) {
    commit('SET_SELECTED_ITEM', item);
  },

  async updateItemOwner({ commit }, { itemId, ownerId }) {
    try {
      const response = await PipelineCardAPI.updateCard(itemId, {
        owner_id: ownerId,
      });
      commit('UPDATE_PIPELINE_CARD_ITEM', response.data);
      commit('SET_LAST_UPDATED');
      return response.data;
    } catch (error) {
      console.error('Failed to update item owner:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  // NOOVI: aplica os filtros do board (server-side). Guarda os filtros, força a
  // invalidação do cache e refetch — o backend retorna já filtrado, então as
  // colunas renderizam direto, sem filtragem client-side (que quebrava com a
  // paginação por stage).
  async setActiveFilters({ commit, dispatch }, filters = {}) {
    commit('SET_ACTIVE_FILTERS', filters || {});
    // Limpa a paginação por-stage (cardsByStage) — os cursores antigos não valem
    // mais com o conjunto filtrado.
    commit('RESET_CARDS_BY_STAGE');
    // Ciclo 3b — o board é o único consumidor de setActiveFilters; o refetch
    // pós-filtro segue o caminho por stage (paralelo, limit 50/coluna).
    return dispatch('fetchBoardByStages');
  },

  // Lista e agenda exibem exatamente o mesmo filtro que alimenta a API. Isso
  // evita que um filtro deixado no kanban continue ocultamente aplicado nas
  // páginas seguintes do cursor global.
  async setGlobalFilters({ commit, dispatch }, filters = {}) {
    commit('SET_ACTIVE_FILTERS', filters || {});
    commit('RESET_CARDS_BY_STAGE');
    commit('SET_LAST_UPDATED', null);
    commit('SET_LAST_FETCHED_MODE', null);
    return dispatch('fetchPipelineCardItems');
  },

  async fetchPipelineCardItems(
    { commit, dispatch, state: localState },
    { append = false } = {}
  ) {
    // Get current user and pipeline to validate cache
    const currentUserId = this.getters.getCurrentUserID;
    const currentPipeline = this.getters['pipelines/getSelectedPipeline'];
    const currentPipelineId = currentPipeline?.id;
    const currentAccountId = accountIdFromUrl();

    // A troca de sessão precisa limpar os cards imediatamente, antes da nova
    // resposta. As chaves/guards abaixo também impedem que requests da sessão
    // anterior voltem a preencher o estado.
    resetIfUserChanged(commit, localState, currentUserId);

    // NOOVI: assinatura dos filtros ativos — muda quando o usuário aplica/limpa
    // filtros, invalidando o cache para forçar um refetch server-side.
    const filtersSignature = JSON.stringify(localState.activeFilters || {});
    const pageCursor = append ? localState.meta?.next_cursor : 'first';
    const globalKey = `${currentUserId}|${currentAccountId}|${currentPipelineId}|${filtersSignature}|${pageCursor}`;

    // Ciclo 7 — o append global usa CURSOR keyset (meta.next_cursor), não
    // offset: o flat pode conter merges do board (fetchStageCards), então
    // `items.length` não corresponde mais a um offset global confiável.
    // Pré-condições do append: o último load foi global (o cursor pertence a
    // uma página global) e o backend sinalizou que há mais.
    if (append) {
      if (
        localState.lastFetchedMode !== 'global' ||
        !localState.meta?.has_more ||
        !localState.meta?.next_cursor
      ) {
        return localState.items;
      }
    }

    // Skip cache check when appending (loading more)
    if (!append) {
      // Verifica se o cache é válido (including user, pipeline, account check)
      // Convert IDs to strings for consistent comparison
      const currentPipelineIdStr = currentPipelineId
        ? String(currentPipelineId)
        : null;
      const lastFetchedPipelineIdStr = localState.lastFetchedPipelineId
        ? String(localState.lastFetchedPipelineId)
        : null;

      const now = new Date().getTime();
      const isCacheValid =
        localState.items.length > 0 &&
        localState.lastUpdated &&
        localState.lastFetchedMode === 'global' && // Board stamp (50/stage) can't satisfy a global-page fetch
        localState.lastFetchedUserId === currentUserId && // Invalidate on user switch
        lastFetchedPipelineIdStr === currentPipelineIdStr && // Invalidate on pipeline switch
        localState.lastFetchedAccountId === currentAccountId && // Invalidate on account switch
        localState.lastFetchedFiltersSignature === filtersSignature && // Invalidate on filter change
        now - localState.lastUpdated < CACHE_DURATION_MS;

      if (isCacheValid) {
        // Retorna os dados cacheados sem mudar o estado de loading
        return localState.items;
      }
    }

    // Dedupe POR CHAVE. Pipeline/filtro/cursor novos geram outra chave e sempre
    // passam; apenas a mesma página em voo é reaproveitada.
    if (inFlightGlobalRequest?.key === globalKey) {
      return inFlightGlobalRequest.promise;
    }

    const globalRequest = { key: globalKey, promise: null };
    inFlightGlobalRequest = globalRequest;
    if (!append) {
      globalGeneration += 1;
      // A página global substitui o flat; stages de um board anterior não
      // podem continuar mergeando depois dela.
      boardGeneration += 1;
      inFlightBoardKey = null;
    }
    const generation = globalGeneration;
    const requestIdentityStillMatches = () => {
      const pipelineIdNow = this.getters['pipelines/getSelectedPipeline']?.id;
      return (
        generation === globalGeneration &&
        String(this.getters.getCurrentUserID ?? '') ===
          String(currentUserId ?? '') &&
        accountIdFromUrl() === currentAccountId &&
        String(pipelineIdNow || '') === String(currentPipelineId || '') &&
        JSON.stringify(localState.activeFilters || {}) === filtersSignature
      );
    };

    // Audit 2026-04-25 — append usa flag separada para não acionar o skeleton
    // (que destrói o DOM da kanban e perde scroll do usuário).
    if (append) {
      commit('SET_APPENDING', true);
    } else {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
    }
    const performRequest = async () => {
      try {
        if (!currentPipelineId) {
          throw new Error('Nenhum pipeline selecionado');
        }

        // Audit 2026-04-25 — backend limita a 500 por request (era 2000).
        // Ciclo 7 — "carregar mais" da LISTA pagina por cursor keyset global
        // (ordenação (position, id), mesmo contrato do board por coluna; o
        // backend emite meta.next_cursor também no caminho offset da página 1).
        // O board NÃO usa este caminho: pagina por coluna via fetchStageCards.
        const params = {
          limit: 500,
          filters: localState.activeFilters,
        };
        if (append) {
          params.cursor = localState.meta.next_cursor;
        } else {
          params.offset = 0;
        }
        const response = await PipelineCardAPI.getCards(
          currentPipelineId,
          params
        );

        // Handle paginated response (data + meta)
        const responseData = response.data;
        const items = extractGlobalItems(responseData);
        const meta = normalizeGlobalMeta(responseData?.meta, items.length);

        // Descarta resposta stale: filtro, pipeline, account ou modo mudaram no
        // meio do request. O fail-closed também cobre a página inicial global;
        // sem isso uma resposta antiga podia substituir o board/lista vencedor.
        if (!requestIdentityStillMatches()) {
          throw globalRequestSupersededError();
        }

        if (meta.has_more) {
          const requestedCursors = new Set();
          if (append && pageCursor) requestedCursors.add(String(pageCursor));
          requireNextPipelineCursor(meta.next_cursor, requestedCursors);
        }

        if (append) {
          // Merge, não append cego: ids já presentes no flat (board/realtime
          // inseriram cards de cursor posterior, ou card movido/reordenado
          // entre páginas) são ATUALIZADOS com os dados mais novos da resposta
          // em vez de descartados (achado Codex ciclo 7).
          commit('MERGE_PIPELINE_CARD_ITEMS', items);
          commit('SET_META', meta);
        } else {
          commit('SET_PIPELINE_CARD_ITEMS', items);
          commit('SET_META', meta);
        }
        // Sequências ativas vêm embutidas no payload do index (ciclo 3a) —
        // semeia o cache do badge e evita o N+1 de GET /cards/:id/sequences
        // que o onMounted de cada PipelineCard dispararia.
        dispatch('pipelineCardSequences/seedFromCards', items, { root: true });
        commit('SET_LAST_UPDATED');
        commit('SET_LAST_FETCHED_USER', currentUserId); // Track which user fetched
        commit('SET_LAST_FETCHED_PIPELINE', currentPipelineId); // Track which pipeline fetched
        commit('SET_LAST_FETCHED_ACCOUNT', currentAccountId); // Track which account fetched
        commit('SET_LAST_FETCHED_FILTERS', filtersSignature); // Track which filters fetched
        commit('SET_LAST_FETCHED_MODE', 'global');
        return items;
      } catch (error) {
        // Only the request that still owns this account/pipeline/filter
        // identity may surface an error. Superseded callers still reject, but
        // cannot overwrite the winning view or emit a misleading toast.
        if (requestIdentityStillMatches()) {
          console.error('[STORE-DEBUG] Erro em fetchPipelineCardItems:', error);
          commit(
            'SET_ERROR',
            error?.code === PIPELINE_PAGINATION_ERROR_CODE
              ? PIPELINE_PAGINATION_ERROR_CODE
              : error.message
          );
          handlePipelineError(error);
        }
        throw error;
      } finally {
        // Object identity, rather than the key string, prevents an old K1
        // request from clearing a newer K1 request after a K1 -> K2 -> K1 race.
        if (inFlightGlobalRequest === globalRequest) {
          inFlightGlobalRequest = null;
          // A newer request can switch modes (append -> reload or reload ->
          // append). The winner owns both flags so the superseded request
          // cannot leave one of them stuck forever.
          commit('SET_APPENDING', false);
          if (!inFlightBoardKey) commit('SET_LOADING', false);
        }
      }
    };

    globalRequest.promise = performRequest();
    return globalRequest.promise;
  },

  // Utilitário explícito para consumidores que realmente precisam do flat
  // global completo. As views interativas de lista/agenda usam paginação sob
  // demanda e não chamam este caminho. O guard de cursor repetido impede loop
  // infinito em uma resposta defeituosa.
  async fetchAllPipelineCardItems({
    commit = () => {},
    dispatch,
    state: localState,
    rootGetters,
  }) {
    const accountId = accountIdFromUrl();
    const pipelineId = rootGetters['pipelines/getSelectedPipeline']?.id;
    const currentUserId = rootGetters.getCurrentUserID;
    const filtersSignature = JSON.stringify(localState.activeFilters || {});
    const fullLoadKey = `${currentUserId}|${accountId}|${pipelineId}|${filtersSignature}`;

    if (inFlightFullLoadKey === fullLoadKey && inFlightFullLoadPromise) {
      return inFlightFullLoadPromise;
    }

    fullLoadGeneration += 1;
    const generation = fullLoadGeneration;
    commit('SET_ERROR', null);
    const loadAllPages = async () => {
      await dispatch('fetchPipelineCardItems');

      const identityStillMatches = () =>
        generation === fullLoadGeneration &&
        (currentUserId === null ||
          currentUserId === undefined ||
          (String(rootGetters.getCurrentUserID ?? '') ===
            String(currentUserId) &&
            String(localState.lastFetchedUserId ?? '') ===
              String(currentUserId))) &&
        localState.lastFetchedMode === 'global' &&
        localState.lastFetchedAccountId === accountId &&
        String(localState.lastFetchedPipelineId || '') ===
          String(pipelineId || '') &&
        localState.lastFetchedFiltersSignature === filtersSignature;

      if (!identityStillMatches()) throw fullLoadSupersededError();

      const seenCursors = new Set();
      while (localState.meta?.has_more) {
        requireNextPipelineCursor(localState.meta?.next_cursor, seenCursors);

        // Cursores são dependentes; cada página precisa terminar antes da próxima.
        // eslint-disable-next-line no-await-in-loop
        await dispatch('fetchPipelineCardItems', { append: true });
        if (!identityStillMatches()) throw fullLoadSupersededError();
      }

      return localState.items;
    };

    const fullLoadPromise = loadAllPages();
    inFlightFullLoadKey = fullLoadKey;
    inFlightFullLoadPromise = fullLoadPromise;
    try {
      return await fullLoadPromise;
    } catch (error) {
      if (
        generation === fullLoadGeneration &&
        error?.code === PIPELINE_PAGINATION_ERROR_CODE
      ) {
        commit('SET_ERROR', PIPELINE_PAGINATION_ERROR_CODE);
      }
      throw error;
    } finally {
      if (inFlightFullLoadPromise === fullLoadPromise) {
        inFlightFullLoadKey = null;
        inFlightFullLoadPromise = null;
      }
    }
  },

  async createPipelineCardItem({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await PipelineCardAPI.createCard(payload);
      commit('ADD_PIPELINE_CARD_ITEM', response.data);
      commit('SET_LAST_UPDATED'); // Invalida cache ao criar
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Hidrata cards que chegaram pelo WebSocket como STUB. O payload de
  // broadcast é magro por design (15 campos escalares — engordá-lo custa banda
  // × N assinantes), então o card remoto entra no board sem contato, labels,
  // valor nem item_details até este fetch. (fase-57 RT-P1-4)
  //
  // Mora no store porque a API de cards importa o próprio store: chamá-la de
  // dentro do helper de cable fecharia um ciclo de import.
  async hydrateRemoteCards({ commit, state: localState }, { ids, pipelineId }) {
    const pendingIds = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!pendingIds.length) return;

    const accountIdAtDispatch = accountIdFromUrl();

    await Promise.all(
      pendingIds.map(async cardId => {
        try {
          const { data: card } = await PipelineCardAPI.getCard(cardId);
          if (!card) return;
          // Trocou de conta ou de pipeline no meio do voo: o card hidratado
          // não pertence mais ao board que está na tela.
          if (accountIdFromUrl() !== accountIdAtDispatch) return;
          if (
            pipelineId != null &&
            String(card.pipeline_id) !== String(pipelineId)
          ) {
            return;
          }
          // O card pode ter sido removido do flat entre o broadcast e a
          // resposta — REPLACE_CARD é no-op nesse caso.
          const index = itemsById.get(card.id);
          if (index === undefined) return;

          // Um evento de WebSocket mais NOVO pode ter chegado enquanto o GET
          // estava no ar (mudança de etapa logo após a criação). Substituir
          // pelo corpo da resposta jogaria o board de volta ao estado antigo
          // (achado do cross-review), então respeita a mesma ordenação por
          // versão que as mutations de realtime usam.
          const toEpochMs = value => {
            if (value == null) return 0;
            if (typeof value === 'number') {
              return value < 1e12 ? value * 1000 : value;
            }
            const parsed = Date.parse(value);
            return Number.isNaN(parsed) ? 0 : parsed;
          };
          const stored = localState.items[index];
          if (toEpochMs(card.updated_at) < toEpochMs(stored?.updated_at)) {
            return;
          }

          commit('REPLACE_CARD', card);
        } catch (error) {
          const status = error?.response?.status;
          // 404/403 são TERMINAIS: o card foi apagado logo depois de criado, ou
          // o agente não pode vê-lo. Deixar o stub na tela mostraria um card
          // que não existe (ou que ele não tem direito de ver) até o próximo
          // fetch — remove agora (achado do cross-review).
          if (status === 404 || status === 403) {
            commit('REMOVE_PIPELINE_CARD_REMOTE', { id: cardId, stage: null });
            return;
          }
          // Transitório (timeout, 5xx): o stub fica e o próximo fetch
          // reconcilia. Não é erro de usuário — nada de toast nem SET_ERROR —
          // mas registra para não sumir em silêncio.
          console.warn(
            `[pipelineCards] hidratação do card ${cardId} falhou`,
            status || error?.message
          );
        }
      })
    );
  },

  // Replace entire card in store (used when card is updated via API)
  replaceCard({ commit }, card) {
    commit('REPLACE_CARD', card);
  },

  async moveItemToStage(
    { commit, dispatch, state: localState },
    { itemId, columnId, lostReason = null, wonValue = null, wonNote = null }
  ) {
    // Garantir que itemId seja número
    const numericItemId = parseInt(itemId, 10);

    // Obter o item original para possível reversão — O(1) via Map
    const originalIdx = itemsById.get(numericItemId);
    const originalItem =
      originalIdx !== undefined ? localState.items[originalIdx] : undefined;

    // Snapshot do baseline ANTES do ajuste otimista: o ajuste reverso não é
    // simétrico quando a chave de origem não existia no mapa (o clamp/criação
    // de chave deixaria um count que o servidor nunca teve — achado Codex).
    const stageCountsSnapshot = localState.meta?.stage_counts
      ? { ...localState.meta.stage_counts }
      : null;

    if (originalItem) {
      // Modificar o item no store antes da API (igual à exclusão)
      commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
      // Ciclo 8 — badge da coluna acompanha o move otimista (decrementa a
      // origem, incrementa o destino); o fetch seguinte reconcilia.
      commit('ADJUST_STAGE_COUNTS', {
        fromStage: originalItem.pipeline_stage,
        toStage: columnId,
      });
    }

    try {
      // Chamar a API passando fromStage para evitar fetch redundante
      const fromStage = originalItem?.pipeline_stage;
      const response = await PipelineCardAPI.moveToStage(
        numericItemId,
        columnId,
        fromStage,
        {
          lostReason,
          wonValue,
          wonNote,
        }
      );
      // Atualizar o card completo no store com a resposta da API
      // moveToStage retorna o card diretamente (não o wrapper axios)
      if (response) {
        commit('REPLACE_CARD', response);
        // Backend pode ter ajustado o stage final (ex.: automação) — move o
        // delta do badge do destino solicitado para o efetivo.
        if (response.pipeline_stage && response.pipeline_stage !== columnId) {
          commit('ADJUST_STAGE_COUNTS', {
            fromStage: columnId,
            toStage: response.pipeline_stage,
          });
        }
      }
      // Invalidar cache do frontend após sucesso
      await dispatch('itemUpdated');
      return true;
    } catch (error) {
      // Em caso de erro, reverter para o estado original
      if (originalItem) {
        commit('UPDATE_ITEM_STAGE', {
          itemId: numericItemId,
          columnId: originalItem.pipeline_stage,
        });
        // Restaura o SNAPSHOT do baseline (não um ajuste reverso): quando a
        // chave de origem não existia, o reverso criaria um count que o
        // servidor nunca teve (achado Codex ciclo 8).
        if (stageCountsSnapshot) {
          commit('RESTORE_STAGE_COUNTS', stageCountsSnapshot);
        }
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async deletePipelineCardItem({ commit, state: localState }, itemId) {
    const originalItem = localState.items.find(i => i.id === itemId);
    commit('REMOVE_PIPELINE_CARD_ITEM', itemId);
    // Board: a lápide da mutation acima. Lista/agenda: aqui — ver
    // invalidateGlobalListRequests.
    invalidateGlobalListRequests();

    try {
      // Pass originalItem to avoid extra getCard call in API
      await PipelineCardAPI.deleteCard(itemId, { currentCard: originalItem });
      commit('SET_LAST_UPDATED'); // Invalida cache ao deletar
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_PIPELINE_CARD_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async updateItemDetails({ commit, state: localState }, { itemId, details }) {
    // Get current item from store (avoid extra API call)
    const currentItem = localState.items.find(i => i.id === itemId);

    // Optimistic update: update store first for faster UI
    commit('UPDATE_ITEM_DETAILS', { itemId, details });

    try {
      // Build payload using store data instead of fetching from API
      const payload = {
        pipeline_id: details.pipeline_id ?? currentItem?.pipeline_id,
        pipeline_stage: details.pipeline_stage ?? currentItem?.pipeline_stage,
        position: details.position ?? currentItem?.position,
        owner_id: details.owner_id ?? currentItem?.owner_id,
        item_details: details.item_details ?? currentItem?.item_details,
        conversation_display_id:
          details.conversation_display_id ??
          currentItem?.conversation_display_id,
        timer_started_at:
          details.timer_started_at ?? currentItem?.timer_started_at,
        timer_duration: details.timer_duration ?? currentItem?.timer_duration,
        custom_attributes:
          details.custom_attributes ?? currentItem?.custom_attributes,
        // Auditoria 2026-08-08 (F09): o formulário envia estes campos na raiz e
        // esta reconstrução os descartava — a edição aparecia na tela (update
        // otimista) e sumia no recarregamento. Todos são aceitos pelo backend.
        //
        // Cross-review Codex: só a presença da chave decide. `??` impediria
        // limpar um campo anulável, e completar com o cache sobrescreveria a
        // edição concorrente de outra pessoa.
        ...pickSupplied(details, [
          'contact_id',
          'title',
          'description',
          'priority',
          'expected_revenue',
          'currency',
          'forecast_close_date',
        ]),
      };

      // Pass currentItem to API for webhook change tracking
      const response = await PipelineCardAPI.updateCard(itemId, payload, {
        currentCard: currentItem,
      });
      return response.data;
    } catch (error) {
      // Revert optimistic update on error
      if (currentItem) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: currentItem });
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  updateLocalItems({ commit }, items) {
    commit('SET_PIPELINE_CARD_ITEMS', items);
    commit('SET_LAST_UPDATED'); // Atualiza o timestamp ao receber dados externos
  },

  async recalculateLeadScore({ commit }, itemId) {
    try {
      const response = await LeadScoresAPI.recalculate(itemId);
      const item = response.data;
      const camelItem = {
        id: itemId,
        lead_score: item.lead_score,
        lead_score_factors: item.lead_score_factors,
        lead_score_category: item.lead_score_category,
        // Auditoria 2026-08-08 (R6): `updated_at` nesta resposta carrega a data
        // do CÁLCULO, não a do card — nome legado que a API pública mantém.
        // `card_updated_at` é o campo certo, e existe nas duas rotas de
        // recálculo.
        updated_at: item.card_updated_at ?? item.updated_at,
      };
      commit('UPDATE_PIPELINE_CARD_ITEM', camelItem);
      return camelItem;
    } catch (error) {
      console.error('Failed to recalculate lead score:', error);
      throw error;
    }
  },

  async overrideLeadScore({ commit }, { itemId, score }) {
    try {
      const response = await LeadScoresAPI.override(itemId, score);
      const item = response.data;
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        lead_score: item.lead_score,
        manual_override: item.manual_override,
      });
      return item;
    } catch (error) {
      console.error('Failed to override lead score:', error);
      throw error;
    }
  },

  async markAsWon({ commit, dispatch }, itemId) {
    try {
      const response = await DealStatusAPI.markAsWon(itemId);
      // Patch the board card with the authoritative payload — including
      // `status` and `pipeline_stage` so the card moves to the won column,
      // not just flips its won_at flag.
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        won_at: response.data.won_at,
        won_value: response.data.won_value,
        lost_at: null,
        lost_reason: null,
        status: response.data.status,
        pipeline_stage: response.data.pipeline_stage,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to mark deal as won:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async markAsLost({ commit, dispatch }, { itemId, reason }) {
    try {
      const response = await DealStatusAPI.markAsLost(itemId, reason);
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        lost_at: response.data.lost_at,
        lost_reason: response.data.lost_reason,
        won_at: null,
        status: response.data.status,
        pipeline_stage: response.data.pipeline_stage,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to mark deal as lost:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async reopenDeal({ commit, dispatch }, itemId) {
    try {
      const response = await DealStatusAPI.reopen(itemId);
      // reopen restores the card to its pre-won/pre-lost regular stage — patch
      // `pipeline_stage` so the board moves it out of the won/lost column.
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        won_at: null,
        lost_at: null,
        lost_reason: null,
        won_value: null,
        status: response.data.status,
        pipeline_stage: response.data.pipeline_stage,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to reopen deal:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  // Invalidate the board cache so the next fetchPipelineCardItems pulls
  // fresh data. Called by surfaces that mutate cards WITHOUT touching the
  // board's local `items` (e.g. PipelineSection in the conversation view,
  // BulkAddModal). Must null `lastUpdated` — stamping it to now() would
  // keep `isCacheValid` true and the board would stay stale until F5.
  async itemUpdated({ commit }) {
    commit('SET_LAST_UPDATED', null);
    return true;
  },

  // Reset cache on logout or user switch
  resetCache({ commit }) {
    // Fix P0 — invalida respostas de board/stage ainda em voo: nada do load
    // anterior ao reset pode mergear num store limpo.
    invalidatePipelineCardRequests();
    commit('SET_PIPELINE_CARD_ITEMS', []);
    commit('SET_META', {
      total: 0,
      limit: 50,
      offset: 0,
      has_more: false,
      stage_counts: {},
      next_cursor: null,
    });
    commit('CLEAR_STAGE_CARDS', null);
    commit('SET_LAST_UPDATED', null);
    commit('SET_LAST_FETCHED_USER', null);
    commit('SET_LAST_FETCHED_PIPELINE', null);
    commit('SET_LAST_FETCHED_ACCOUNT', null);
    commit('SET_ACTIVE_FILTERS', {});
    commit('SET_LAST_FETCHED_FILTERS', null);
    commit('SET_LAST_FETCHED_MODE', null);
    commit('SET_LOADING', false);
    commit('SET_APPENDING', false);
  },

  // Audit 2026-05-07 — wipe full state when user switches between accounts.
  // Called by App.vue's watcher on `currentAccountId` change. Without this,
  // cards loaded for account A linger in `state.items` and components
  // mounted briefly during route transition fire requests with cardA IDs
  // against the new account's URL → backend returns 404 (protective) but
  // the console gets noisy.
  resetForAccountSwitch({ commit }) {
    // Fix P0 — mesma regra do resetCache: respostas em voo da conta anterior
    // nunca podem mergear no estado limpo da nova conta.
    invalidatePipelineCardRequests();
    commit('RESET_FOR_ACCOUNT_SWITCH');
  },

  resetForUserSwitch({ commit }) {
    invalidatePipelineCardRequests();
    commit('RESET_FOR_USER_SWITCH');
  },

  // P1.1 / Ciclo 3b — fetch cards de UM stage específico com cursor pagination
  // keyset (position, id). Os ITEMS entram no array flat (`items`, fonte de
  // verdade única — drag-and-drop, realtime e bulk continuam intactos) via
  // merge com dedupe por id; `cardsByStage` guarda APENAS o metadado de
  // paginação do stage ({ cursor, hasMore, loading }).
  async fetchStageCards(
    { commit, dispatch, state: localState, rootGetters },
    {
      pipelineId,
      stageId,
      append = false,
      limit = 50,
      generation = null,
      // Cross-review Codex: um retry de UMA coluna não pode transformar-se em
      // erro global do board — quem decide isso é o coordenador do board.
      stageScoped = false,
      currentUserId = rootGetters?.getCurrentUserID,
    } = {}
  ) {
    if (!pipelineId || !stageId) {
      throw new Error('pipelineId e stageId são obrigatórios');
    }
    // Anti-stale por GERAÇÃO + assinatura de filtros. O default
    // `?? boardGeneration` cobre o "carregar mais" da coluna (append avulso):
    // um load de board novo incrementa a geração e invalida appends em voo.
    // Fix P0: NÃO usar o getter de pipeline selecionado aqui — durante
    // remount/transição de rota ele passa por janela null/stale e descartava
    // respostas válidas (colunas presas em "Carregando itens...").
    const gen = generation ?? boardGeneration;
    const filtersSignature = JSON.stringify(localState.activeFilters || {});
    commit('SET_STAGE_LOADING', { stageId, loading: true });
    // Nova tentativa limpa o erro anterior desta coluna.
    commit('SET_STAGE_ERROR', { stageId, error: null });

    try {
      const stageState = localState.cardsByStage[stageId];
      const cursor = append && stageState ? stageState.cursor : null;
      const params = {
        pipeline_id: pipelineId,
        pipeline_stage: stageId,
        limit,
        filters: localState.activeFilters,
      };
      if (cursor) params.cursor = cursor;

      const response = await PipelineCardAPI.getCards(pipelineId, params);
      const responseData = response.data;
      const items = responseData.data || responseData;
      const meta = responseData.meta || {};

      // Descarta resposta stale: outra geração de board assumiu (troca de
      // pipeline/conta, reset) ou os filtros mudaram no meio do voo.
      const isStale =
        gen !== boardGeneration ||
        String(rootGetters?.getCurrentUserID ?? '') !==
          String(currentUserId ?? '') ||
        JSON.stringify(localState.activeFilters || {}) !== filtersSignature;
      if (isStale) return items;

      commit('MERGE_PIPELINE_CARD_ITEMS', items);
      commit('SET_STAGE_PAGINATION', {
        stageId,
        cursor: meta.next_cursor || null,
        hasMore: !!meta.has_more,
      });
      // Sequências embutidas no payload (ciclo 3a) — evita o N+1 de
      // GET /cards/:id/sequences no onMounted de cada card.
      dispatch('pipelineCardSequences/seedFromCards', items, { root: true });

      // stage_counts filtrado vem em toda resposta — mantém o badge da coluna.
      if (meta.stage_counts) {
        commit('SET_META', {
          ...localState.meta,
          stage_counts: meta.stage_counts,
        });
      }
      return items;
    } catch (error) {
      const stillOwnsRequest =
        gen === boardGeneration &&
        String(rootGetters?.getCurrentUserID ?? '') ===
          String(currentUserId ?? '') &&
        JSON.stringify(localState.activeFilters || {}) === filtersSignature;
      if (stillOwnsRequest) {
        if (!stageScoped) commit('SET_ERROR', error.message);
        // F22: registra no PRÓPRIO stage para a coluna poder mostrar erro e
        // oferecer "tentar de novo" em vez de girar indefinidamente.
        commit('SET_STAGE_ERROR', { stageId, error: error.message, append });
        handlePipelineError(error);
      }
      throw error;
    } finally {
      commit('SET_STAGE_LOADING', { stageId, loading: false });
    }
  },

  // Ciclo 3b — orquestradora do load do board (kanban): paginação server-side
  // real POR COLUNA. Substitui o fetch global de limit:500 (1,52s/446KB com
  // 400 cards e teto silencioso >500 cards). Dispara fetchStageCards em
  // PARALELO (limit 50/stage) somente para os stages VISÍVEIS do pipeline;
  // cada coluna pagina depois pelo próprio cursor (loadMoreFromAPI). O flat
  // continua sendo a fonte de verdade dos items.
  async fetchBoardByStages(
    { commit, dispatch, state: localState, rootGetters },
    { limit = 50 } = {}
  ) {
    const pipeline = rootGetters['pipelines/getSelectedPipeline'];
    const pipelineId = pipeline?.id;
    if (!pipelineId) return [];

    const currentUserId = rootGetters.getCurrentUserID;
    const currentAccountId = accountIdFromUrl();
    resetIfUserChanged(commit, localState, currentUserId);
    const filtersSignature = JSON.stringify(localState.activeFilters || {});

    // Entrar no board invalida qualquer orquestração global, inclusive quando
    // o board abaixo puder responder do cache.
    globalGeneration += 1;
    inFlightGlobalRequest = null;
    // A global append superseded by the board can no longer clear its own
    // flag in finally because ownership moved away from that request.
    commit('SET_APPENDING', false);
    fullLoadGeneration += 1;
    inFlightFullLoadKey = null;
    inFlightFullLoadPromise = null;

    // Cache de 5min com a MESMA chave do fetch global + modo 'board' — um
    // carimbo de board (50/stage) nunca satisfaz list/agenda e vice-versa
    // (um carimbo global não tem cardsByStage; o board precisa dos cursores).
    const now = new Date().getTime();
    const isCacheValid =
      localState.items.length > 0 &&
      localState.lastUpdated &&
      localState.lastFetchedMode === 'board' &&
      localState.lastFetchedUserId === currentUserId &&
      String(localState.lastFetchedPipelineId || '') === String(pipelineId) &&
      localState.lastFetchedAccountId === currentAccountId &&
      localState.lastFetchedFiltersSignature === filtersSignature &&
      now - localState.lastUpdated < CACHE_DURATION_MS;
    if (isCacheValid) {
      // A global request may have owned `loading=true` immediately before the
      // cached board superseded it. Its finally can no longer clear the flag
      // after ownership changes, so the cache winner must release it here.
      commit('SET_LOADING', false);
      return localState.items;
    }

    // Guard de concorrência POR CHAVE: um load idêntico em andamento é
    // deduplicado, mas trocar pipeline/conta/filtro SUPERSEDE o load antigo
    // (sem isso, trocar para o pipeline B durante o load de A devolvia o
    // board de B vazio até outro trigger — achado Codex).
    const boardKey = `${currentUserId}|${currentAccountId}|${pipelineId}|${filtersSignature}`;
    if (localState.loading && inFlightBoardKey === boardKey) {
      return localState.items;
    }
    inFlightBoardKey = boardKey;

    // Fix P0 — nova geração de board: invalida respostas em voo do load
    // anterior (stage fetches E appends de coluna). Incrementa DEPOIS dos
    // early-returns de cache/dedupe — um dispatch deduplicado não pode
    // invalidar o load em andamento no qual ele se apoia.
    boardGeneration += 1;
    const generation = boardGeneration;

    commit('SET_LOADING', true);
    // Limpa erro de um load anterior — sucesso parcial/total deve renderizar.
    commit('SET_ERROR', null);
    try {
      // Board novo: limpa o flat e a paginação por stage do load anterior.
      commit('SET_PIPELINE_CARD_ITEMS', []);
      commit('RESET_CARDS_BY_STAGE');
      commit('SET_LAST_FETCHED_MODE', null);

      const stageIds = visibleStageIds(pipeline);
      const results = await Promise.allSettled(
        stageIds.map(stageId =>
          dispatch('fetchStageCards', {
            pipelineId,
            stageId,
            limit,
            generation,
            currentUserId,
          })
        )
      );

      // Anti-stale: outro load assumiu (troca de pipeline/conta — geração
      // mudou) ou os filtros mudaram no meio → NÃO carimba o cache. Crítico:
      // os fetchStageCards stale resolvem fulfilled (descartam o merge mas
      // não rejeitam), então sem o check de geração um load SUPERSEDED via
      // allSettled "0 falhas" carimbava o cache do próprio pipeline POR CIMA
      // do vencedor — voltar a ele dava cache hit com items de outro pipeline
      // e board vazio sem nenhuma request (QA re-teste, cenário 2).
      if (
        generation !== boardGeneration ||
        String(rootGetters.getCurrentUserID ?? '') !==
          String(currentUserId ?? '') ||
        JSON.stringify(localState.activeFilters || {}) !== filtersSignature
      ) {
        return localState.items;
      }

      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length === 0) {
        // Carimba o cache só quando TODOS os stages resolveram — falha
        // parcial deixa o cache inválido para o próximo load tentar de novo.
        commit('SET_LAST_UPDATED');
        commit('SET_LAST_FETCHED_USER', currentUserId);
        commit('SET_LAST_FETCHED_PIPELINE', pipelineId);
        commit('SET_LAST_FETCHED_ACCOUNT', currentAccountId);
        commit('SET_LAST_FETCHED_FILTERS', filtersSignature);
        commit('SET_LAST_FETCHED_MODE', 'board');
      } else if (failures.length < results.length) {
        // Falha parcial: renderiza o que carregou (fetchStageCards setou o
        // erro do stage que falhou; sem limpar, o board inteiro viraria a
        // tela de erro mesmo com N-1 colunas válidas).
        commit('SET_ERROR', null);
      }
      return localState.items;
    } finally {
      // Um load superseded (outra chave assumiu) não pode apagar o loading
      // do load mais novo.
      if (inFlightBoardKey === boardKey) {
        inFlightBoardKey = null;
        commit('SET_LOADING', false);
      }
    }
  },

  async updateQualificationChecklist({ commit }, { itemId, checklist }) {
    try {
      const response = await PipelineCardAPI.updateQualificationChecklist(
        itemId,
        checklist
      );
      const item = response.data;
      commit('UPDATE_QUALIFICATION_CHECKLIST', {
        itemId,
        checklist: item.qualification_checklist,
        qualificationScore: item.qualification_score,
        leadScore: item.lead_score,
        leadScoreFactors: item.lead_score_factors,
      });
      commit('SET_LAST_UPDATED');
      return item;
    } catch (error) {
      console.error('Failed to update qualification checklist:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },
};

const resetUserScopedState = _state => {
  _state.items = [];
  _state.cardsByStage = {};
  _state.selectedItem = null;
  _state.meta = {
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false,
    stage_counts: {},
    next_cursor: null,
  };
  _state.lastUpdated = null;
  _state.lastFetchedUserId = null;
  _state.lastFetchedPipelineId = null;
  _state.lastFetchedAccountId = null;
  _state.activeFilters = {};
  _state.lastFetchedFiltersSignature = null;
  _state.lastFetchedMode = null;
  _state.error = null;
  _state.loading = false;
  _state.appending = false;
  itemsById.clear();
  removedItemIds.clear();
};

const mutations = {
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_APPENDING(_state, appending) {
    _state.appending = appending;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },
  SET_SELECTED_ITEM(_state, item) {
    _state.selectedItem = item;
  },
  // P0.2 — Object.freeze nos items individuais. Vue 3 detecta frozen e
  // pula a instalação de proxies reativos profundos. Para 2000+ cards com
  // item_details aninhado, isso elimina ~50-200ms de overhead na carga
  // inicial e em cada update reativo.
  // P0.7 — rebuilda o índice itemsById para lookup O(1) nas mutations.
  SET_PIPELINE_CARD_ITEMS(_state, items) {
    const frozen = items.map(item => Object.freeze(item));
    _state.items = frozen;
    rebuildIndex(frozen);
    // Snapshot completo do servidor = época nova (ver removedItemIds).
    removedItemIds.clear();
  },
  SET_META(_state, meta) {
    _state.meta = meta;
  },
  // Ciclo 3b — merge com dedupe por id no array flat (fonte de verdade dos
  // items do board). Item já existente é substituído (dados mais novos do
  // backend); novos são anexados ao fim. Mantém o índice O(1) consistente e
  // troca a referência do array (contrato dos watchers — ver setItemAt).
  MERGE_PIPELINE_CARD_ITEMS(_state, items) {
    if (!items || !items.length) return;
    let next = _state.items;
    const toAppend = [];
    items.forEach(item => {
      // Resposta em voo de antes do DELETE: ignora o card já excluído em vez de
      // re-anexá-lo (ver removedItemIds).
      if (removedItemIds.has(item.id)) return;
      const idx = itemsById.get(item.id);
      if (idx === undefined) {
        toAppend.push(Object.freeze(item));
        itemsById.set(item.id, next.length + toAppend.length - 1);
      } else if (idx < next.length) {
        next = setItemAt(next, idx, item);
      } else {
        // Dupe dentro do MESMO batch (id repetido): substitui no pendente.
        toAppend[idx - next.length] = Object.freeze(item);
      }
    });
    _state.items = toAppend.length ? [...next, ...toAppend] : next;
  },
  // Ciclo 7: APPEND_PIPELINE_CARD_ITEMS removida — o append global da lista
  // usa MERGE_PIPELINE_CARD_ITEMS + SET_META (ids existentes são atualizados,
  // não descartados; achado Codex).
  ADD_PIPELINE_CARD_ITEM(_state, item) {
    const frozen = Object.freeze(item);
    // O card voltou a existir para o board: rollback de um delete que falhou,
    // restauração da lixeira ou evento realtime de criação. Sem tirar a lápide,
    // o próximo MERGE o descartaria de novo.
    removedItemIds.delete(frozen.id);
    _state.items = [..._state.items, frozen];
    itemsById.set(frozen.id, _state.items.length - 1);
    // Badge da coluna (meta.stage_counts → backendStageCount em
    // PipelineColumn.vue) é incrementado otimistamente p/ o stage do novo card.
    // Senão o contador ficava defasado até o próximo fetch (QA 2026-06-12: criar
    // card deixava 4 cards na coluna com o badge ainda em "3"). Os callers de
    // ADD são create (success) e o rollback de delete — em ambos o incremento é
    // correto; o drag usa UPDATE_ITEM_STAGE + ADJUST_STAGE_COUNTS (sem ADD), logo
    // não há dupla contagem. adjustStageCounts já pula sob filtros server-side.
    adjustStageCounts(_state, null, frozen.pipeline_stage);
  },
  REMOVE_PIPELINE_CARD_ITEM(_state, itemId) {
    const removed = _state.items.find(item => item.id === itemId);
    _state.items = _state.items.filter(item => item.id !== itemId);
    rebuildIndex(_state.items);
    // Lápide: barra a re-anexação por respostas de GET disparadas antes deste
    // delete (ver removedItemIds). Sempre marcada, mesmo quando o card não
    // estava carregado — a resposta em voo pode trazê-lo pela primeira vez.
    removedItemIds.add(itemId);
    // Decrementa o badge do stage do card removido (mesmo motivo do ADD; o
    // delete commitava REMOVE sem ajustar o contador → badge alto até o fetch).
    if (removed?.pipeline_stage) {
      adjustStageCounts(_state, removed.pipeline_stage, null);
    }
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    const numericItemId = parseInt(itemId, 10);
    const itemIndex = itemsById.get(numericItemId);

    if (itemIndex !== undefined && itemIndex !== -1) {
      const item = _state.items[itemIndex];
      _state.items = setItemAt(_state.items, itemIndex, {
        ...item,
        pipeline_stage: columnId,
      });
    }
  },
  // Ciclo 8 — ajuste otimista do badge da coluna (meta.stage_counts) quando
  // um card muda de stage no fluxo de drag (move otimista, revert em erro,
  // reconciliação pós-API). O servidor segue autoritativo: o próximo fetch
  // re-emite o mapa completo e corrige qualquer drift.
  ADJUST_STAGE_COUNTS(_state, { fromStage, toStage }) {
    adjustStageCounts(_state, fromStage, toStage);
  },
  // Restaura o baseline capturado antes de um ajuste otimista que falhou —
  // o ajuste reverso não é simétrico quando a chave de origem não existia.
  RESTORE_STAGE_COUNTS(_state, snapshot) {
    _state.meta = { ..._state.meta, stage_counts: { ...snapshot } };
  },
  REPLACE_CARD(_state, card) {
    const itemIndex = itemsById.get(card.id);
    if (itemIndex !== undefined && itemIndex !== -1) {
      _state.items = setItemAt(_state.items, itemIndex, card);
    }
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        item_structure: structure,
      });
    }
  },
  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const item = _state.items[index];
      const updatedFields = {};
      Object.keys(details).forEach(key => {
        if (key in item) {
          updatedFields[key] = details[key];
        } else if (key === 'item_details') {
          updatedFields.item_details = {
            ...item.item_details,
            ...details.item_details,
          };
        }
      });
      _state.items = setItemAt(_state.items, index, {
        ...item,
        ...updatedFields,
      });
    }
  },
  // Pass an explicit value (e.g. null) to set it directly; omit the
  // argument to stamp "now". Passing null marks the cache as stale so the
  // next fetchPipelineCardItems performs a real refetch (used by
  // itemUpdated / resetCache). Previously the argument was ignored and the
  // value was always stamped to now(), which kept the cache "valid" and
  // caused the board to show stale data until a full page reload.
  SET_LAST_UPDATED(_state, timestamp = undefined) {
    _state.lastUpdated =
      timestamp === undefined ? new Date().getTime() : timestamp;
  },

  SET_LAST_FETCHED_USER(_state, userId) {
    _state.lastFetchedUserId = userId;
  },

  SET_LAST_FETCHED_PIPELINE(_state, pipelineId) {
    _state.lastFetchedPipelineId = pipelineId;
  },

  SET_LAST_FETCHED_ACCOUNT(_state, accountId) {
    _state.lastFetchedAccountId = accountId;
  },

  SET_ACTIVE_FILTERS(_state, filters) {
    _state.activeFilters = filters || {};
  },

  SET_LAST_FETCHED_FILTERS(_state, signature) {
    _state.lastFetchedFiltersSignature = signature;
  },

  SET_LAST_FETCHED_MODE(_state, mode) {
    _state.lastFetchedMode = mode;
  },

  RESET_CARDS_BY_STAGE(_state) {
    _state.cardsByStage = {};
  },

  RESET_FOR_ACCOUNT_SWITCH(_state) {
    resetUserScopedState(_state);
  },

  RESET_FOR_USER_SWITCH(_state) {
    resetUserScopedState(_state);
  },

  UPDATE_PIPELINE_CARD_ITEM(_state, item) {
    const index = itemsById.get(item.id);
    if (index !== undefined && index !== -1) {
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...item,
      });
    }
  },

  UPDATE_QUALIFICATION_CHECKLIST(
    _state,
    { itemId, checklist, qualificationScore, leadScore, leadScoreFactors }
  ) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const updatedFields = {
        qualification_checklist: checklist,
        qualification_score: qualificationScore,
      };
      if (leadScore !== undefined) {
        updatedFields.lead_score = leadScore;
      }
      if (leadScoreFactors !== undefined) {
        updatedFields.lead_score_factors = leadScoreFactors;
      }
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...updatedFields,
      });
    }
  },

  UPDATE_LEAD_SCORE(
    _state,
    { itemId, leadScore, leadScoreFactors, leadScoreCategory }
  ) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const updatedFields = { lead_score: leadScore };
      if (leadScoreFactors !== undefined) {
        updatedFields.lead_score_factors = leadScoreFactors;
      }
      if (leadScoreCategory !== undefined) {
        updatedFields.lead_score_category = leadScoreCategory;
      }
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...updatedFields,
      });
    }
  },

  // === REAL-TIME MUTATIONS (from WebSocket) ===

  // Add card from remote (other user created)
  ADD_PIPELINE_CARD_REMOTE(_state, card) {
    // Check if card already exists (dedupe)
    if (!itemsById.has(card.id) && card.id) {
      // Convert snake_case from server to match local format
      const normalizedCard = {
        id: card.id,
        pipeline_stage: card.stage || card.pipeline_stage,
        position: card.position,
        owner_id: card.owner_id,
        conversation_display_id: card.conversation_display_id,
        title: card.title,
        updated_at: card.updated_at,
        won_at: card.won_at,
        lost_at: card.lost_at,
        lead_score: card.lead_score,
        // Preserve any additional fields that might come from full card data
        ...card,
      };
      const frozen = Object.freeze(normalizedCard);
      // Criação/restauração remota devolve o card ao board: tira a lápide, senão
      // o próximo MERGE voltaria a descartá-lo (ver removedItemIds).
      removedItemIds.delete(frozen.id);
      _state.items = [..._state.items, frozen];
      itemsById.set(frozen.id, _state.items.length - 1);
      // O badge da coluna vinha do servidor e não era ajustado aqui: card novo
      // entrava no board e a contagem continuava a de antes até o próximo
      // fetch. adjustStageCounts com origem nula só incrementa o destino.
      // (fase-57 RT-P1-5)
      if (frozen.pipeline_stage) {
        adjustStageCounts(_state, null, frozen.pipeline_stage);
      }
    }
  },

  // Update card from remote (other user updated)
  UPDATE_PIPELINE_CARD_REMOTE(_state, { card }) {
    const index = itemsById.get(card.id);
    if (index !== undefined && index !== -1) {
      const existing = _state.items[index];
      // updated_at can arrive in TWO formats:
      //   - REST API (initial fetch): ISO 8601 string "2026-05-07T00:31:00.000Z"
      //   - WebSocket broadcast (BroadcastService): epoch SECONDS (number)
      // Coerce both to epoch ms for safe comparison. A naive `>=` between
      // a number and a string returns false, which silently dropped every
      // realtime update on the kanban board.
      const toEpochMs = v => {
        if (v == null) return 0;
        if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? 0 : ms;
      };
      const existingVersion = toEpochMs(existing.updated_at);
      const incomingVersion = toEpochMs(card.updated_at);

      // Only update if incoming version is newer or equal (to handle concurrent updates)
      if (incomingVersion >= existingVersion) {
        // Ciclo 8 — won/lost/updated remotos podem auto-mover o card de stage;
        // ajusta o badge da coluna junto (no-op quando o stage não muda).
        adjustStageCounts(
          _state,
          existing.pipeline_stage,
          card.stage || card.pipeline_stage || existing.pipeline_stage
        );
        // Normalize and merge — preserve local fields when broadcast payload is partial
        const normalizedCard = {
          ...existing,
          pipeline_stage:
            card.stage || card.pipeline_stage || existing.pipeline_stage,
          position: card.position ?? existing.position,
          owner_id: card.owner_id ?? existing.owner_id,
          title: card.title ?? existing.title,
          updated_at: incomingVersion,
          won_at: card.won_at !== undefined ? card.won_at : existing.won_at,
          lost_at: card.lost_at !== undefined ? card.lost_at : existing.lost_at,
          lost_reason: card.lost_reason ?? existing.lost_reason,
          lead_score: card.lead_score ?? existing.lead_score,
          deadline: card.deadline ?? existing.deadline,
          scheduled_at: card.scheduled_at ?? existing.scheduled_at,
          status: card.status ?? existing.status,
          priority: card.priority ?? existing.priority,
        };
        _state.items = setItemAt(_state.items, index, normalizedCard);
        syncSelectedItem(_state, card.id, normalizedCard);
      }
    }
  },

  // Remove card from remote (other user deleted).
  // Aceita o id puro (contrato antigo) ou `{ id, stage }`: o board é PAGINADO,
  // então o card apagado pode não estar carregado — nesse caso a etapa vem do
  // payload do broadcast, senão o badge da coluna continuaria contando um card
  // que não existe mais (fase-57 RT-P1-5 + achado do cross-review).
  REMOVE_PIPELINE_CARD_REMOTE(_state, payload) {
    const isDetailed = payload !== null && typeof payload === 'object';
    const cardId = isDetailed ? payload.id : payload;
    const stageFromEvent = isDetailed ? payload.stage : null;

    // A etapa local precisa ser lida ANTES do filter — depois o card já saiu
    // do array.
    const index = itemsById.get(cardId);
    const removedStage =
      index !== undefined && index !== -1
        ? _state.items[index]?.pipeline_stage
        : stageFromEvent;

    _state.items = _state.items.filter(item => item.id !== cardId);
    rebuildIndex(_state.items);
    // Mesma lápide do REMOVE local: a exclusão remota (outro usuário, bulk, ou
    // o eco do próprio delete) também corre com GETs em voo.
    removedItemIds.add(cardId);

    if (removedStage) {
      adjustStageCounts(_state, removedStage, null);
    }
    clearSelectedItemIfRemoved(_state, cardId);
  },

  // Update card stage from remote (other user moved card)
  UPDATE_CARD_STAGE_REMOTE(_state, { cardId, stage, position, version }) {
    const index = itemsById.get(cardId);
    if (index !== undefined && index !== -1) {
      const existing = _state.items[index];
      // Same dual-format problem as UPDATE_PIPELINE_CARD_REMOTE — coerce both.
      const toMs = v => {
        if (v == null) return 0;
        if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? 0 : ms;
      };
      const existingVersion = toMs(existing.updated_at);
      const versionMs = toMs(version);

      // Only update if incoming version is newer
      if (versionMs >= existingVersion) {
        // Ciclo 8 — move remoto (outro usuário): badge da coluna acompanha.
        adjustStageCounts(_state, existing.pipeline_stage, stage);
        const movedCard = {
          ...existing,
          pipeline_stage: stage,
          position: position ?? existing.position,
          updated_at: version,
        };
        _state.items = setItemAt(_state.items, index, movedCard);
        syncSelectedItem(_state, cardId, movedCard);
      }
    }
  },

  // Reorder cards from remote (other user reordered)
  REORDER_CARDS_REMOTE(_state, { positions, version }) {
    if (!positions || !Array.isArray(positions)) return;

    // Acumular todas as trocas antes de reatribuir para evitar múltiplos
    // re-renders. Usar reduce para ir construindo o array novo a cada posição.
    let updatedItems = _state.items;
    positions.forEach(pos => {
      const index = itemsById.get(pos.id);
      if (index !== undefined && index !== -1) {
        const existing = updatedItems[index];
        updatedItems = setItemAt(updatedItems, index, {
          ...existing,
          position: pos.position,
          pipeline_stage:
            pos.pipeline_stage || pos.stage || existing.pipeline_stage,
          updated_at: version || existing.updated_at,
        });
      }
    });
    _state.items = updatedItems;
  },

  // Update labels for cards linked to a conversation
  UPDATE_CARD_LABELS_BY_CONVERSATION(
    _state,
    { conversationDisplayId, labels }
  ) {
    let changed = false;
    let updatedItems = _state.items;
    _state.items.forEach((item, index) => {
      if (item.conversation_display_id === conversationDisplayId) {
        const existing = updatedItems[index];
        // Update label_list in item_details.conversation
        const updatedItemDetails = {
          ...existing.item_details,
          conversation: {
            ...(existing.item_details?.conversation || {}),
            label_list: labels,
          },
        };
        updatedItems = setItemAt(updatedItems, index, {
          ...existing,
          item_details: updatedItemDetails,
        });
        changed = true;
      }
    });
    if (changed) {
      _state.items = updatedItems;
    }
  },

  // === P1.1 / Ciclo 3b — CURSOR PAGINATION BY STAGE ===
  // Metadado de paginação per-stage ({ cursor, hasMore, loading }); os items
  // vivem só no flat (MERGE_PIPELINE_CARD_ITEMS). Cursor keyset (28× mais
  // rápido que offset no worst-case 2.442-card stage).
  SET_STAGE_PAGINATION(_state, { stageId, cursor, hasMore }) {
    const existing = _state.cardsByStage[stageId] || { loading: false };
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: {
        ...existing,
        cursor: cursor || null,
        hasMore: hasMore || false,
      },
    };
  },
  // Auditoria 2026-08-08 (F22): numa falha PARCIAL do board o erro global é
  // limpo de propósito (senão N-1 colunas boas virariam tela de erro), mas nada
  // registrava QUAL coluna falhou. Ela ficava "carregando" para sempre, e o
  // retry automático nem tentava porque `hasMore` nasceu falso.
  SET_STAGE_ERROR(_state, { stageId, error, append = false }) {
    const existing = _state.cardsByStage[stageId] || {
      cursor: null,
      hasMore: false,
      loading: false,
    };
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: {
        ...existing,
        error: error || null,
        // Cross-review Codex: o retry precisa repetir o MESMO request. Se a
        // falha foi num "carregar mais", recomeçar da página 1 descartaria a
        // paginação já carregada.
        errorAppend: error ? append : false,
      },
    };
  },
  SET_STAGE_LOADING(_state, { stageId, loading }) {
    const existing = _state.cardsByStage[stageId] || {
      cursor: null,
      hasMore: false,
    };
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: { ...existing, loading },
    };
  },
  CLEAR_STAGE_CARDS(_state, stageId) {
    if (stageId) {
      const next = { ..._state.cardsByStage };
      delete next[stageId];
      _state.cardsByStage = next;
    } else {
      _state.cardsByStage = {};
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
